// MOCK: entire file. Delete when the real Google Identity Services flow lands.
/**
 * Fixtures for the simulated Google OAuth consent flow (demo mode only).
 *
 * Nothing here ever touches Google. The host string is deliberately
 * `accounts.google.example` — it reads correctly in a screenshot but is an
 * unregistrable reserved TLD, so a leaked screenshot can't be mistaken for,
 * or phish against, the real consent screen.
 */

export const MOCK_OAUTH_HOST = "accounts.google.example";
export const MOCK_OAUTH_URL = `${MOCK_OAUTH_HOST}/o/oauth2/auth`;

/** The genuine scope string — shown to technical buyers, never requested here. */
export const ANALYTICS_READONLY_SCOPE = "https://www.googleapis.com/auth/analytics.readonly";

/** Every timing in one place so the flow can be tuned, or zeroed in tests. */
export const MOCK_DELAYS = {
    launchPopup: 450,
    accountSelect: 600,
    consentProcessing: 800,
    popupExit: 220,
    wizardConnecting: 1200
};

export interface MockGoogleAccount {
    id: string;
    name: string;
    email: string;
    initials: string;
    /** Avatar circle fill — initials only, never a photo. */
    color: string;
}

export const MOCK_ACCOUNTS: MockGoogleAccount[] = [
    {
        id: "acct-work",
        name: "Priya Sharma",
        email: "priya@thrivestack.com",
        initials: "PS",
        color: "#2F54EB"
    },
    {
        id: "acct-personal",
        name: "Priya Sharma",
        email: "priya.sharma@gmail.com",
        initials: "PS",
        color: "#5B9A5E"
    }
];

/**
 * Everything here comes from the `property_metadata` stream — no extra API call.
 * `propertyId` is shown, never hidden behind a hover: it is the number support
 * asks for, and the number that appears in every sync failure message.
 */
export interface MockGa4Property {
    id: string;
    /** Numeric GA4 property ID, e.g. "411273625". */
    propertyId: string;
    name: string;
    kind: "GA4" | "UA";
    /** GA4's own timezone — date boundaries follow this, not the viewer's. */
    timeZone: string;
    currencyCode: string;
    sessionsLast30d: number | null;
    supported: boolean;
    /** Reason shown inline when `supported` is false. */
    unsupportedReason?: string;
}

export interface MockPropertyAccount {
    accountName: string;
    accountId: string;
    properties: MockGa4Property[];
}

/** Grouped account → property, the way GA4 actually structures them. */
export const MOCK_PROPERTY_ACCOUNTS: MockPropertyAccount[] = [
    {
        accountName: "ThriveStack Corporation",
        accountId: "105560568",
        properties: [
            {
                id: "properties/411273625",
                propertyId: "411273625",
                name: "Thrivestack Website",
                kind: "GA4",
                timeZone: "America/Los_Angeles",
                currencyCode: "USD",
                sessionsLast30d: 284109,
                supported: true
            },
            {
                id: "properties/411290117",
                propertyId: "411290117",
                name: "ThriveStack Docs",
                kind: "GA4",
                timeZone: "America/Los_Angeles",
                currencyCode: "USD",
                sessionsLast30d: 41882,
                supported: true
            },
            {
                id: "properties/UA-88213-1",
                propertyId: "UA-88213-1",
                name: "ThriveStack Blog (legacy)",
                kind: "UA",
                timeZone: "America/Los_Angeles",
                currencyCode: "USD",
                sessionsLast30d: null,
                supported: false,
                unsupportedReason: "Universal Analytics is not supported"
            }
        ]
    },
    {
        accountName: "ThriveStack EU",
        accountId: "112904471",
        properties: [
            {
                id: "properties/401229337",
                propertyId: "401229337",
                name: "ThriveStack DE",
                kind: "GA4",
                timeZone: "Europe/Berlin",
                currencyCode: "EUR",
                sessionsLast30d: 9204,
                supported: true
            }
        ]
    }
];

export function findPropertiesByIds(ids: string[]): MockGa4Property[] {
    const wanted = new Set(ids);
    return MOCK_PROPERTY_ACCOUNTS.flatMap((a) => a.properties).filter((p) => wanted.has(p.id));
}

export function findPropertyById(id: string | null): MockGa4Property | null {
    if (!id) return null;
    for (const account of MOCK_PROPERTY_ACCOUNTS) {
        const hit = account.properties.find((p) => p.id === id);
        if (hit) return hit;
    }
    return null;
}

/** Search only appears once the list gets long enough to need it. */
export const PROPERTY_SEARCH_THRESHOLD = 5;

export function countProperties(accounts: MockPropertyAccount[]): number {
    return accounts.reduce((total, a) => total + a.properties.length, 0);
}

export type OAuthErrorCode = "DENIED" | "POPUP_CLOSED" | "NO_PROPERTIES" | "INSUFFICIENT_PERMISSION";

export const OAUTH_ERROR_CODES: OAuthErrorCode[] = [
    "DENIED",
    "POPUP_CLOSED",
    "NO_PROPERTIES",
    "INSUFFICIENT_PERMISSION"
];
